import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch, parseApiResponse } from '../api';
import { toast } from '../utils/toast';
import '../styles/admin-dashboard.css';

const DATASETS = ['students', 'sessions', 'transcripts', 'tutors', 'capacity', 'assessments'];

function formatDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState({ items: [], total: 0, limit: 20, offset: 0 });
  const [bugs, setBugs] = useState({ items: [], total: 0, limit: 20, offset: 0 });
  const [selectedBug, setSelectedBug] = useState(null);
  const [dataset, setDataset] = useState('students');
  const [datasetRows, setDatasetRows] = useState({ items: [], total: 0, limit: 20, offset: 0 });
  const [activityFilters, setActivityFilters] = useState({ actor_email: '', event_type: '' });
  const [bugFilters, setBugFilters] = useState({ status: '', severity: '', source: '' });
  const [loading, setLoading] = useState({
    summary: false,
    activity: false,
    bugs: false,
    dataset: false,
    bugDetail: false,
  });

  const loadSummary = useCallback(async () => {
    setLoading(prev => ({ ...prev, summary: true }));
    try {
      const response = await apiFetch('/api/admin/dashboard/summary');
      const result = await parseApiResponse(response, 'admin.summary');
      if (!result.ok) throw new Error(result.error?.message || 'Failed to load summary');
      setSummary(result.data.summary || null);
    } catch (err) {
      toast.error(err.message || 'Failed to load admin summary');
    } finally {
      setLoading(prev => ({ ...prev, summary: false }));
    }
  }, []);

  const loadActivity = useCallback(async () => {
    setLoading(prev => ({ ...prev, activity: true }));
    try {
      const params = new URLSearchParams({ limit: '20', offset: '0' });
      if (activityFilters.actor_email) params.set('actor_email', activityFilters.actor_email);
      if (activityFilters.event_type) params.set('event_type', activityFilters.event_type);
      const response = await apiFetch(`/api/admin/activity?${params.toString()}`);
      const result = await parseApiResponse(response, 'admin.activity');
      if (!result.ok) throw new Error(result.error?.message || 'Failed to load activity');
      setActivity(result.data || { items: [], total: 0, limit: 20, offset: 0 });
    } catch (err) {
      toast.error(err.message || 'Failed to load activity');
    } finally {
      setLoading(prev => ({ ...prev, activity: false }));
    }
  }, [activityFilters.actor_email, activityFilters.event_type]);

  const loadBugs = useCallback(async () => {
    setLoading(prev => ({ ...prev, bugs: true }));
    try {
      const params = new URLSearchParams({ limit: '20', offset: '0' });
      if (bugFilters.status) params.set('status', bugFilters.status);
      if (bugFilters.severity) params.set('severity', bugFilters.severity);
      if (bugFilters.source) params.set('source', bugFilters.source);
      const response = await apiFetch(`/api/admin/bugs?${params.toString()}`);
      const result = await parseApiResponse(response, 'admin.bugs');
      if (!result.ok) throw new Error(result.error?.message || 'Failed to load bugs');
      setBugs(result.data || { items: [], total: 0, limit: 20, offset: 0 });
    } catch (err) {
      toast.error(err.message || 'Failed to load bugs');
    } finally {
      setLoading(prev => ({ ...prev, bugs: false }));
    }
  }, [bugFilters.severity, bugFilters.source, bugFilters.status]);

  const loadDataset = useCallback(async (targetDataset) => {
    setLoading(prev => ({ ...prev, dataset: true }));
    try {
      const response = await apiFetch(`/api/admin/data/${targetDataset}?limit=20&offset=0`);
      const result = await parseApiResponse(response, `admin.dataset.${targetDataset}`);
      if (!result.ok) throw new Error(result.error?.message || 'Failed to load dataset');
      setDatasetRows(result.data || { items: [], total: 0, limit: 20, offset: 0 });
    } catch (err) {
      toast.error(err.message || `Failed to load ${targetDataset}`);
      setDatasetRows({ items: [], total: 0, limit: 20, offset: 0 });
    } finally {
      setLoading(prev => ({ ...prev, dataset: false }));
    }
  }, []);

  const loadBugDetail = useCallback(async (id) => {
    if (!id) return;
    setLoading(prev => ({ ...prev, bugDetail: true }));
    try {
      const response = await apiFetch(`/api/admin/bugs/${id}`);
      const result = await parseApiResponse(response, `admin.bug.${id}`);
      if (!result.ok) throw new Error(result.error?.message || 'Failed to load bug detail');
      setSelectedBug(result.data.bug || null);
    } catch (err) {
      toast.error(err.message || 'Failed to load bug detail');
    } finally {
      setLoading(prev => ({ ...prev, bugDetail: false }));
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  useEffect(() => {
    loadBugs();
  }, [loadBugs]);

  useEffect(() => {
    loadDataset(dataset);
  }, [dataset, loadDataset]);

  const datasetColumns = useMemo(() => {
    const first = datasetRows.items?.[0] || {};
    return Object.keys(first).slice(0, 8);
  }, [datasetRows.items]);

  const updateSelectedBug = async () => {
    if (!selectedBug?.id) return;
    try {
      const response = await apiFetch(`/api/admin/bugs/${selectedBug.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: selectedBug.status,
          severity: selectedBug.severity,
          assigned_to_email: selectedBug.assigned_to_email || null,
          admin_notes: selectedBug.admin_notes || null,
        }),
      });
      const result = await parseApiResponse(response, `admin.bug.update.${selectedBug.id}`);
      if (!result.ok) throw new Error(result.error?.message || 'Failed to update bug report');
      toast.success('Bug report updated');
      await Promise.all([loadBugs(), loadBugDetail(selectedBug.id), loadSummary()]);
    } catch (err) {
      toast.error(err.message || 'Failed to update bug report');
    }
  };

  return (
    <div className="admin-dashboard">
      <section className="admin-section">
        <h1>Admin Operations Dashboard</h1>
        <p>Visibility into captain activity, tracked data, and platform bugs.</p>
      </section>

      <section className="admin-section">
        <h2>Overview</h2>
        {loading.summary && <p>Loading summary...</p>}
        {!loading.summary && summary && (
          <div className="admin-cards">
            <div className="admin-card">
              <div className="label">Active Team Captains ({summary.window_days}d)</div>
              <div className="value">{summary.active_team_captains ?? 0}</div>
            </div>
            <div className="admin-card">
              <div className="label">Transcript Sync (Success / Fail)</div>
              <div className="value">{summary.transcript_sync?.synced ?? 0} / {summary.transcript_sync?.failed ?? 0}</div>
            </div>
            <div className="admin-card">
              <div className="label">Session Volume (Transcripts Window)</div>
              <div className="value">{summary.session_volume?.transcripts_window ?? 0}</div>
            </div>
            <div className="admin-card">
              <div className="label">Open Bugs</div>
              <div className="value">{summary.bugs?.open_count ?? 0}</div>
            </div>
          </div>
        )}
        {summary?.bugs?.recent_critical?.length > 0 && (
          <div className="admin-table-wrap">
            <h3>Recent Critical Issues</h3>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {summary.bugs.recent_critical.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.title || item.description || 'Untitled'}</td>
                    <td>{item.status}</td>
                    <td>{formatDate(item.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="admin-section">
        <div className="admin-section-header">
          <h2>Team Captain Activity</h2>
          <div className="admin-controls">
            <input
              placeholder="Actor email"
              value={activityFilters.actor_email}
              onChange={(e) => setActivityFilters(prev => ({ ...prev, actor_email: e.target.value }))}
            />
            <input
              placeholder="Event type"
              value={activityFilters.event_type}
              onChange={(e) => setActivityFilters(prev => ({ ...prev, event_type: e.target.value }))}
            />
            <button className="lrbrain-btn lrbrain-btn-outline" onClick={loadActivity}>Refresh</button>
          </div>
        </div>
        <div className="admin-table-wrap">
          {loading.activity && <p>Loading activity...</p>}
          {!loading.activity && (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Role</th>
                  <th>Event</th>
                  <th>Entity</th>
                </tr>
              </thead>
              <tbody>
                {(activity.items || []).map((item) => (
                  <tr key={item.id}>
                    <td>{formatDate(item.created_at)}</td>
                    <td>{item.actor_email || '-'}</td>
                    <td>{item.actor_role || '-'}</td>
                    <td>{item.event_type}</td>
                    <td>{item.entity_type || '-'}:{item.entity_id || '-'}</td>
                  </tr>
                ))}
                {(activity.items || []).length === 0 && (
                  <tr><td colSpan={5}>No activity events found.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="admin-section">
        <div className="admin-section-header">
          <h2>Tracked Data</h2>
          <select value={dataset} onChange={(e) => setDataset(e.target.value)}>
            {DATASETS.map((item) => (
              <option key={item} value={item}>{item}</option>
            ))}
          </select>
        </div>
        <div className="admin-table-wrap">
          {loading.dataset && <p>Loading dataset...</p>}
          {!loading.dataset && (
            <table className="admin-table">
              <thead>
                <tr>
                  {datasetColumns.map((col) => <th key={col}>{col}</th>)}
                </tr>
              </thead>
              <tbody>
                {(datasetRows.items || []).map((row, idx) => (
                  <tr key={`${dataset}-${idx}`}>
                    {datasetColumns.map((col) => <td key={col}>{String(row[col] ?? '-')}</td>)}
                  </tr>
                ))}
                {(datasetRows.items || []).length === 0 && (
                  <tr><td colSpan={Math.max(datasetColumns.length, 1)}>No records found.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="admin-section">
        <div className="admin-section-header">
          <h2>Bug Reports</h2>
          <div className="admin-controls">
            <select
              value={bugFilters.status}
              onChange={(e) => setBugFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="">all status</option>
              <option value="open">open</option>
              <option value="investigating">investigating</option>
              <option value="resolved">resolved</option>
            </select>
            <select
              value={bugFilters.severity}
              onChange={(e) => setBugFilters(prev => ({ ...prev, severity: e.target.value }))}
            >
              <option value="">all severity</option>
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
              <option value="critical">critical</option>
            </select>
            <select
              value={bugFilters.source}
              onChange={(e) => setBugFilters(prev => ({ ...prev, source: e.target.value }))}
            >
              <option value="">all sources</option>
              <option value="manual">manual</option>
              <option value="frontend_auto">frontend_auto</option>
              <option value="backend_auto">backend_auto</option>
            </select>
            <button className="lrbrain-btn lrbrain-btn-outline" onClick={loadBugs}>Refresh</button>
          </div>
        </div>
        <div className="admin-grid">
          <div className="admin-table-wrap">
            {loading.bugs && <p>Loading bug reports...</p>}
            {!loading.bugs && (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Severity</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Title</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  {(bugs.items || []).map((item) => (
                    <tr key={item.id} onClick={() => loadBugDetail(item.id)} className="clickable-row">
                      <td>{item.id}</td>
                      <td>{item.severity}</td>
                      <td>{item.status}</td>
                      <td>{item.source}</td>
                      <td>{item.title || item.description || 'Untitled'}</td>
                      <td>{formatDate(item.created_at)}</td>
                    </tr>
                  ))}
                  {(bugs.items || []).length === 0 && (
                    <tr><td colSpan={6}>No bug reports found.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>

          <div className="admin-bug-detail">
            <h3>Bug Detail</h3>
            {!selectedBug && <p>Select a bug report to review and update.</p>}
            {loading.bugDetail && <p>Loading bug detail...</p>}
            {selectedBug && !loading.bugDetail && (
              <div className="bug-form">
                <label>
                  <span>ID</span>
                  <input value={selectedBug.id} readOnly />
                </label>
                <label>
                  <span>Status</span>
                  <select
                    value={selectedBug.status || 'open'}
                    onChange={(e) => setSelectedBug(prev => ({ ...prev, status: e.target.value }))}
                  >
                    <option value="open">open</option>
                    <option value="investigating">investigating</option>
                    <option value="resolved">resolved</option>
                  </select>
                </label>
                <label>
                  <span>Severity</span>
                  <select
                    value={selectedBug.severity || 'medium'}
                    onChange={(e) => setSelectedBug(prev => ({ ...prev, severity: e.target.value }))}
                  >
                    <option value="low">low</option>
                    <option value="medium">medium</option>
                    <option value="high">high</option>
                    <option value="critical">critical</option>
                  </select>
                </label>
                <label>
                  <span>Assigned To</span>
                  <input
                    value={selectedBug.assigned_to_email || ''}
                    onChange={(e) => setSelectedBug(prev => ({ ...prev, assigned_to_email: e.target.value }))}
                  />
                </label>
                <label>
                  <span>Admin Notes</span>
                  <textarea
                    rows={5}
                    value={selectedBug.admin_notes || ''}
                    onChange={(e) => setSelectedBug(prev => ({ ...prev, admin_notes: e.target.value }))}
                  />
                </label>
                <div className="bug-description">
                  <strong>Description:</strong> {selectedBug.description || '-'}
                </div>
                <button className="lrbrain-btn lrbrain-btn-solid" onClick={updateSelectedBug}>
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
